/* eslint-disable multiline-comment-style */
import React, { useRef, useMemo, useState, useEffect } from 'react'
import { debounce } from 'lodash'
import { useTranslation } from '@sailor/i18n-web'
import Loading from '@roo/roo/Loading'
import Selector from '@roo/roo/Selector'
import type { NormalOption, SelectorOption } from '@roo/roo/Selector'
import styles from './index.module.less'
import { getLanguage } from '@/common/utils/i18n'
import { loadMapScript } from './utils'
import { IAMap } from './types'
import { useEffectOnece } from '@/hooks'
import { useThrottle } from 'ahooks'
import { owlAddJsError } from '@/common/utils/owl'
import { getRegion } from '@/common/utils/i18nFuncs'

interface CoordMarker {
  lat: number | string
  lng: number | string
  icon: string
  visible: boolean
}

interface Props {
  search?: boolean,
  reSearch?: boolean, // 标识重新请求了搜索
  drag?: boolean,
  searchText?: string,
  lang?: string,

  /** 距离范围 */
  circle?: number,
  // 蜂窝范围
  aorArea?: ({
    color: string
    pointStr: string
  })[],
  // 上报点
  markerList?: CoordMarker[],
  icon?: string,
  value: {longitude: string, latitude: string, addressName?: string},
  onChange?: (value:Props['value']) => void
}
// 点标记地图，支持拖拽标记、搜索
function MarkMap (props: Props) {
  const {
    search = false,
    drag = false,
    searchText,
    lang = getLanguage(),
    aorArea,
    markerList,
    icon,
    value, onChange,
  } = props
  const [AMapIns, setAMapIns] = useState<IAMap | undefined>()
  const propsVal = props.value || {}
  const { t } = useTranslation()
  const mapRef = useRef<IAMap>()
  const [isLoading, setIsLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState([])
  const [popupVisible, setPopupVisible] = useState(false)
  const throtedRadius = useThrottle(Number(props.circle), { wait: 300 }) // 减缓地图重刷新
  const mapUtilsRef = useRef({ map: null, circle: null, geocoder: null, AMapIns })


  useEffect(() => {
    if (mapRef.current) {
      setIsLoading(true)
      loadMapScript(
        () => {
          // @ts-ignore
          const newMapIns = AMapIns || window.AMap as IAMap
          try {
            if (newMapIns) {
              newMapIns.setCoordtypeType(newMapIns.WGS84)
              handleMapInsChange(newMapIns)
              changeMap(newMapIns)
              setIsLoading(false)
            }
          } catch (error) {
            owlAddJsError(error)
            console.log(error)
          }
        }
      )
    }
  }, [propsVal.latitude, propsVal.longitude])

  function handleMapInsChange (newIns: IAMap) {
    setAMapIns(newIns)
    mapUtilsRef.current.AMapIns = newIns
  }

  useEffect(() => {
    loadCircle(AMapIns, throtedRadius)
  }, [throtedRadius]) // 半径变更,更新圆圈

  const placeSearch = useMemo(() => {
    if (AMapIns) {
      return new AMapIns.PlaceSearch({
        // map,
        lang: lang,
        location: new AMapIns.LngLat(Number(propsVal.longitude || 114.16), Number(propsVal.latitude || 22.27)),
        autoFitView: true,
      })
    }
  }, [AMapIns])

  const handleSearch = (value: string, init?: boolean) => {
    if (value && placeSearch) {
      placeSearch.search(value, function (status: any, result: any) {
        // 查询成功时，result即对应匹配的POI信息
        if (status === 'complete') {
          const { pois } = result.poiList
          setOptions(pois)
          if (init) {
            const { location } = pois[0] // NOTE: 业务使用wgs84
            setInputValue(pois[0])
            handleChange(location)
          }
        }
        // nextToken = result.nextPageToken;
      })
    }
  }
  useEffectOnece(() => {
    handleSearch(searchText as string, true)
    setPopupVisible(true)
  }, Boolean(search && searchText  && AMapIns))

  function handleChange (location: any, addressName?: string) {
    const mapIns = mapUtilsRef.current.AMapIns // 使用最新值

    const newLoc = { longitude: location.getLng(), latitude: location.getLat() }
    console.log(mapIns)
    if (addressName || !mapIns) {
      onChange?.({ ...newLoc, addressName: addressName || '' })
    } else {
      // NOTE: 根据经纬度反编码名称, 解析出来的位置不一定准 https://demo.sankuai.com/topic/b59bdeaf-8b64-44d2-8c02-70eb762a0b53/a11159b8-419b-4f59-a47e-809175cefa1b
      mapUtilsRef.current.geocoder?.getAddress?.(new mapIns.LngLat(newLoc.longitude, newLoc.latitude), (status: 'complete', result: any) => {
        let addressName = ''
        if (status === 'complete') {
          addressName = result?.regeocode?.formattedAddress
        }
        onChange?.({ ...newLoc, addressName })
      })

    }
  }

  function changeMap () {
    const mapIns = mapUtilsRef.current.AMapIns // 使用最新值
    const centerLocation  = new mapIns.LngLat(Number(propsVal.longitude || 114.16), Number(propsVal.latitude || 22.27))
    const linePathList = aorArea?.map(item => {
      const linePath = item.pointStr.split(';')
      return {
        color: item.color,
        path: linePath.map(lineItem => {
          const point = lineItem.split(',')
          return new mapIns.LngLat(point[1], point[0])
        }),
      }
    })
    if (!mapIns) return
    mapIns.setRegion && mapIns.setRegion(getRegion())
    const map = new mapIns.Map(mapRef.current, {
      zoom: 17,
      zooms: [2, 26],
      lang: lang,
      center: centerLocation,
    })

    let geocoder = new mapIns.Geocoder({
      extensions: 'all',
      // language: 'ja'
    })
    mapUtilsRef.current.map = map // 记录地图实例
    mapUtilsRef.current.geocoder = geocoder // 显示文字实例

    linePathList?.forEach(item => (
      new mapIns.Polygon({
        map: map,
        // 填充透明度
        fillOpacity: 0.,
        // 填充颜色
        // fillColor: 'yellow',
        strokeColor: item.color,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        path: item.path,
      })
    ))
    const toolbar = new mapIns.ToolBar({
      zoomControl: true,
      position: 'RB',
    })
    map && map.addControl(toolbar)

    let Icon = ''
    if (icon) {
      try {
        Icon = new mapIns.Icon({
          image: icon,
          imageSize: new mapIns.Size(36, 36),
          size: new mapIns.Size(36, 36),
        })
      } catch (error) {
        console.error(error)
      }
    }
    const marker = new mapIns.Marker({
      position: centerLocation,
      // offset: new AMap.Pixel(0, 0),
      // raiseOnDrag: true,
      draggable: drag,
      zIndex: 1,
      icon: Icon,
    })
    marker && marker.setMap(map)

    if (markerList && markerList.length) {
      markerList.forEach((item) => {
        const position = new mapIns.LngLat(item.lng, item.lat)
        const icon = new mapIns.Icon({
          image: item.icon,
          imageSize: new mapIns.Size(36, 36),
          size: new mapIns.Size(36, 36),
        })

        const marker = new mapIns.Marker({
          position,
          zIndex: 1,
          visible: item.visible,
          icon,
        })
        marker && marker.setMap(map)
      })
    }

    loadCircle(mapIns, props.circle)
    if (drag) {
      marker && marker.on('dragend', (value: any) => {
        const location = value.lnglat // NOTE: 业务使用wgs84
        handleChange(location)
      })
    }


  }

  function loadCircle (mapIns, circle: number) {
    if (Number(circle) > 0 && mapIns) {
      const centerLocation  = new mapIns.LngLat(Number(propsVal.longitude || 114.16), Number(propsVal.latitude || 22.27))
      const circle = new mapIns.Circle({
        center: centerLocation,
        radius: props.circle, // 半径
        strokeColor: '#FF33FF',
        strokeWeight: 6,
        strokeOpacity: 0.2,
        fillOpacity: 0.4,
        strokeStyle: 'dashed',
        fillColor: '#1791fc',
        zIndex: 50,
      })
      if (circle) {
        if (mapUtilsRef.current.circle) {
          mapUtilsRef.current.circle.setMap(null) // 清除已有范围
        }
        mapUtilsRef.current.circle = circle
        circle.setMap(mapUtilsRef.current.map)
      }
    }
  }

  return (
    <div className={styles.main}>
      {
        AMapIns && search && <Selector
          className={styles.select}
          value={inputValue}
          options={options}
          clearable
          filterable
          rawOption
          showSelectIcon={false}
          placeholder={t('e_pc_search_location', '搜索地点')}
          popupVisible={popupVisible}
          onPopupVisibleChange={(visible: boolean) => setPopupVisible(visible)}
          fieldNames={{
            value: 'id',
            label: 'name',
          }}
          suffixIcon="search"
          onSearch={debounce(handleSearch, 500)} // 输入变更
          onEnter={(e: any, keyword: string) => handleSearch(keyword)}
          onChange={(value: string, raw: any) => {
            if (value) {
              const { location } = raw // NOTE: 业务使用wgs84
              setInputValue(value)
              handleChange(location, raw.address)
              //   marker.moveTo([lng, lat], 1000)
            } else {
              setInputValue('')
              setPopupVisible(false)
            }
          }} // 选择确认
          optionRenderer={(normalized: NormalOption, rawOption: SelectorOption) => <div className={styles.testEllipsis}>{`${rawOption.name}(${rawOption.address})`}</div>}
        />
      }
      <div ref={mapRef} className={styles.map}></div>
      {isLoading && (
        <div style={{ position: 'absolute', top: '48%', left: '48%' }}>
          <Loading visible  text=" " />
        </div>
      )}
    </div>
  )
}
export default MarkMap
